import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PhotoUpload } from '../components/PhotoUpload';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { logger } from '../utils/logger';
import { useUnsavedChangesWarning, confirmDiscard } from '../hooks/useUnsavedChangesWarning';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nombres de las etapas
const STAGE_NAMES = {
  1: 'Recepción',
  2: 'Inspección en Taller',
  3: 'Refacciones Requeridas'
};

export const ExtinguisherStagePage = () => {
  const { extId, stageNum } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get('view') === 'true' || user?.role === 'client';
  const isAdmin = user?.role === 'admin';
  const editMode = searchParams.get('edit') === 'true' && isAdmin;
  const [extinguisher, setExtinguisher] = useState(null);
  const [extinguisherNumber, setExtinguisherNumber] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [stageData, setStageData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const signatureRef = useRef(null);

  // Warn on tab close/refresh if there are unsaved changes.
  // Only active when the form is editable and user has interacted.
  useUnsavedChangesWarning(isDirty && !submitting && !(viewOnly && !editMode));

  const fetchExtinguisher = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/extinguishers/${extId}`);
      setExtinguisher(response.data);

      // Fetch siblings to determine this extinguisher's position (#1, #2, ...)
      try {
        const siblings = await axios.get(`${API}/extinguishers`, {
          params: { order_id: response.data.order_id }
        });
        const idx = siblings.data.findIndex((e) => e.id === extId);
        if (idx >= 0) setExtinguisherNumber(idx + 1);
      } catch (siblingErr) {
        logger.warn('Could not compute extinguisher number', siblingErr);
      }

      const existingData = response.data[`stage_${stageNum}_data`];
      if (existingData) {
        setStageData(existingData);
      } else {
        initializeStageData(parseInt(stageNum));
      }
      
      const existingPhotos = response.data[`stage_${stageNum}_photos`] || [];
      setPhotos(existingPhotos);
    } catch (error) {
      logger.error('Error fetching extinguisher:', error);
      toast.error('Error al cargar el extintor');
    }
  }, [extId, stageNum]);

  useEffect(() => {
    fetchExtinguisher();
  }, [fetchExtinguisher]);

  const initializeStageData = (stage) => {
    if (stage === 1) {
      // Etapa 1: Recepción — Inspección Visual + Resultado
      setStageData({
        // Inspección Visual (Tiene / No tiene / No aplica)
        manijas: '',
        manguera_boquilla: '',
        abrazadera: '',
        manometro_visual: '',
        cola_de_rata: '',
        abolladuras: '',
        seguro_galvanizado: '',
        // Resultado de la Inspección Visual (Sí / No / N/A)
        requiere_recarga: '',
        requiere_prueba_hidrostatica: '',
        observaciones: ''
      });
    } else if (stage === 2) {
      // Etapa 2: Inspección en Taller
      setStageData({
        resorte_valvula: '',
        tubo_sifon_valvula: '',
        manometro_valvula: '',
        vastago: '',
        empaque_cuello: '',
        observaciones: ''
      });
    } else if (stage === 3) {
      // Etapa 3: Refacciones + Fechas SIGAL + Holograma
      setStageData({
        // Refacciones (orden del formato del cliente)
        resorte: '',
        tubo_sifon: '',
        manometro: '',
        vastago: '',
        empaque_cuello: '',
        abrazadera: '',
        seguro_galv: '',
        manguera_boquilla: '',
        // Fechas de recarga / prueba hidrostática SIGAL (movidas desde Etapa 2)
        fecha_recarga_sigal: '',
        fecha_prueba_hidrostatica_sigal: '',
        detalles_prueba_hidrostatica: '',
        // Identificación
        numero_holograma: '',
        // Cierre
        observaciones: '',
        firma_tecnico: ''
      });
    }
  };

  // Wrapper that marks form as dirty whenever data changes.
  const updateStageData = (updater) => {
    setIsDirty(true);
    setStageData((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  // Navigate helper that prompts confirmation when leaving with unsaved changes.
  const safeNavigateBack = () => {
    if (isDirty && !confirmDiscard()) return;
    navigate(`/orders/${extinguisher.order_id}`);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      updateStageData({ ...stageData, firma_tecnico: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let finalStageData = { ...stageData };

      // Si es etapa 3, capturar firma del técnico
      if (stageNum === '3' && signatureRef.current && !signatureRef.current.isEmpty()) {
        finalStageData.firma_tecnico = signatureRef.current.toDataURL();
      }

      // Usar endpoint diferente si es edición de admin
      const endpoint = editMode
        ? `${API}/admin/extinguishers/${extId}/stage/${stageNum}/edit`
        : `${API}/extinguishers/${extId}/stage/${stageNum}`;

      await axios.put(endpoint, {
        stage_data: finalStageData,
        photos: photos
      });

      toast.success(editMode
        ? `Etapa ${stageNum} editada exitosamente`
        : `Etapa ${stageNum} completada exitosamente`
      );
      setIsDirty(false);
      navigate(`/orders/${extinguisher.order_id}`);
    } catch (error) {
      logger.error('Error updating stage:', error);
      // Backend responde 422 con lista de campos faltantes si la validación falla
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 422 && detail?.missing_fields) {
        toast.error(
          `Faltan campos obligatorios: ${detail.missing_fields.join(', ')}`,
          { duration: 6000 }
        );
      } else {
        toast.error('Error al guardar la etapa');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!extinguisher) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <p className="text-lg">Cargando...</p>
        </div>
      </Layout>
    );
  }

  const canEdit = !viewOnly || editMode;
  const stageName = STAGE_NAMES[parseInt(stageNum)] || `Etapa ${stageNum}`;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={safeNavigateBack}
            data-testid="back-button"
            className="hover:bg-secondary"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
              {viewOnly && !editMode ? 'Ver ' : ''}{editMode ? 'Editar ' : ''}Etapa {stageNum}: {stageName}
            </h1>
            <p className="text-base text-muted-foreground mt-2" data-testid="extinguisher-header">
              {extinguisherNumber ? `Extintor #${extinguisherNumber}: ` : 'Extintor: '}
              {extinguisher.agent_type} - {extinguisher.capacity_value} {extinguisher.capacity_unit}
            </p>
            {viewOnly && !editMode && (
              <p className="text-sm text-muted-foreground mt-1">
                Modo solo lectura
              </p>
            )}
            {editMode && (
              <p className="text-sm text-amber-600 font-medium mt-1">
                Modo edición (Admin)
              </p>
            )}
            {viewOnly && !editMode && extinguisher[`stage_${stageNum}_completed_at`] && (
              <div className="mt-3 p-3 bg-secondary border border-border">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  Completada por:
                </p>
                <p className="text-sm font-semibold mt-1">
                  {extinguisher[`stage_${stageNum}_completed_by_name`]}
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-1">
                  {new Date(extinguisher[`stage_${stageNum}_completed_at`]).toLocaleString('es-MX', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="stage-form">
          <div className="p-8 border border-border bg-background space-y-6">
            
            {/* ========== ETAPA 1: RECEPCIÓN ========== */}
            {stageNum === '1' && (
              <>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  Inspección Visual
                </h2>
                <p className="text-sm text-muted-foreground">
                  Selecciona el estado de cada elemento
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'manijas', label: 'Manijas' },
                    { key: 'manguera_boquilla', label: 'Manguera / Boquilla' },
                    { key: 'abrazadera', label: 'Abrazadera' },
                    { key: 'manometro_visual', label: 'Manómetro' },
                    { key: 'cola_de_rata', label: 'Cola de Rata' },
                    { key: 'abolladuras', label: 'Abolladuras en Cilindro' },
                    { key: 'seguro_galvanizado', label: 'Seguro Galvanizado' }
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                        {label} <span className="text-destructive">*</span>
                      </Label>
                      <select
                        value={stageData[key] || ''}
                        onChange={(e) => canEdit && updateStageData({ ...stageData, [key]: e.target.value })}
                        disabled={!canEdit}
                        data-testid={`select-${key}`}
                        className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      >
                        <option value="">— Selecciona —</option>
                        <option value="Tiene">Tiene</option>
                        <option value="No tiene">No tiene</option>
                        <option value="No aplica">No aplica</option>
                      </select>
                    </div>
                  ))}
                </div>
                
                {/* Resultado de la Inspección Visual */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <h3 className="text-lg font-semibold">Resultado de la Inspección Visual</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'requiere_recarga', label: 'Requiere Mtto y Recarga' },
                      { key: 'requiere_prueba_hidrostatica', label: '¿Requiere Prueba Hidrostática?' }
                    ].map(({ key, label }) => {
                      const raw = stageData[key];
                      const displayValue = raw === true ? 'Sí' : raw === false ? 'No' : (raw || '');
                      return (
                        <div key={key} className="space-y-2">
                          <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                            {label} <span className="text-destructive">*</span>
                          </Label>
                          <select
                            value={displayValue}
                            onChange={(e) => canEdit && updateStageData({ ...stageData, [key]: e.target.value })}
                            disabled={!canEdit}
                            data-testid={`select-${key}`}
                            className="w-full px-4 py-2 border border-border bg-background text-foreground"
                          >
                            <option value="">— Selecciona —</option>
                            <option value="Sí">Sí</option>
                            <option value="No">No</option>
                            <option value="N/A">No aplica</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Observaciones */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                    Observaciones <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    value={stageData.observaciones || ''}
                    onChange={(e) => canEdit && updateStageData({ ...stageData, observaciones: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Observaciones adicionales..."
                    className="min-h-[100px]"
                    data-testid="observaciones-input"
                  />
                </div>
              </>
            )}

            {/* ========== ETAPA 2: INSPECCIÓN EN TALLER ========== */}
            {stageNum === '2' && (
              <>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  Inspección Válvula en Taller (Bueno/Dañado/N/A)
                </h2>
                <div className="space-y-4">
                  {[
                    { key: 'resorte_valvula', label: 'Resorte' },
                    { key: 'tubo_sifon_valvula', label: 'Tubo Sifón' },
                    { key: 'manometro_valvula', label: 'Manómetro' },
                    { key: 'vastago', label: 'Vástago' },
                    { key: 'empaque_cuello', label: 'Empaque en Cuello' }
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                        {label} <span className="text-destructive">*</span>
                      </Label>
                      <select
                        value={stageData[key] || ''}
                        onChange={(e) => canEdit && updateStageData({ ...stageData, [key]: e.target.value })}
                        disabled={!canEdit}
                        data-testid={`select-${key}`}
                        className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      >
                        <option value="">— Selecciona —</option>
                        <option value="bueno">Bueno</option>
                        <option value="dañado">Dañado</option>
                        <option value="n/a">N/A</option>
                      </select>
                    </div>
                  ))}
                </div>
                
                {/* Observaciones */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                    Observaciones <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    value={stageData.observaciones || ''}
                    onChange={(e) => canEdit && updateStageData({ ...stageData, observaciones: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Observaciones adicionales..."
                    className="min-h-[100px]"
                    data-testid="observaciones-input"
                  />
                </div>
              </>
            )}

            {/* ========== ETAPA 3: REFACCIONES REQUERIDAS ========== */}
            {stageNum === '3' && (
              <>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  Refacciones Requeridas (Requiere/No Requiere/N/A)
                </h2>
                <p className="text-sm text-muted-foreground">
                  Indica el estado de cada refacción
                </p>
                <div className="space-y-4">
                  {[
                    { key: 'resorte', label: 'Resorte' },
                    { key: 'tubo_sifon', label: 'Tubo Sifón' },
                    { key: 'manometro', label: 'Manómetro' },
                    { key: 'vastago', label: 'Vástago' },
                    { key: 'empaque_cuello', label: 'Empaque en Cuello' },
                    { key: 'abrazadera', label: 'Abrazadera' },
                    { key: 'seguro_galv', label: 'Seguro Galvanizado' },
                    { key: 'manguera_boquilla', label: 'Manguera' }
                  ].map(({ key, label }) => {
                    // Convertir valor booleano antiguo a string para compatibilidad
                    const currentValue = stageData[key];
                    let displayValue = '';
                    if (currentValue === true) displayValue = 'requiere';
                    else if (currentValue === false) displayValue = 'no_requiere';
                    else if (currentValue === 'n/a') displayValue = 'n/a';
                    else if (typeof currentValue === 'string') displayValue = currentValue;
                    return (
                      <div key={key} className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                          {label} <span className="text-destructive">*</span>
                        </Label>
                        <select
                          value={displayValue}
                          onChange={(e) => {
                            if (!canEdit) return;
                            const v = e.target.value;
                            const newValue = v === 'requiere' ? true :
                                            v === 'no_requiere' ? false :
                                            v === 'n/a' ? 'n/a' : '';
                            updateStageData({ ...stageData, [key]: newValue });
                          }}
                          disabled={!canEdit}
                          data-testid={`select-${key}`}
                          className="w-full px-4 py-2 border border-border bg-background text-foreground"
                        >
                          <option value="">— Selecciona —</option>
                          <option value="requiere">Requiere</option>
                          <option value="no_requiere">No Requiere</option>
                          <option value="n/a">N/A</option>
                        </select>
                      </div>
                    );
                  })}
                </div>

                {/* Fechas SIGAL (movidas desde Etapa 2) */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <h3 className="text-lg font-semibold">Fechas de Recarga SIGAL</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                        Fecha de Recarga SIGAL <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={stageData.fecha_recarga_sigal || ''}
                        onChange={(e) => canEdit && updateStageData({ ...stageData, fecha_recarga_sigal: e.target.value })}
                        disabled={!canEdit}
                        data-testid="fecha-recarga-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                        Fecha Prueba Hidrostática (SIGAL)
                        {extinguisher?.stage_1_data?.requiere_prueba_hidrostatica === 'Sí' && (
                          <span className="text-destructive"> *</span>
                        )}
                      </Label>
                      <Input
                        type="date"
                        value={stageData.fecha_prueba_hidrostatica_sigal || ''}
                        onChange={(e) => canEdit && updateStageData({ ...stageData, fecha_prueba_hidrostatica_sigal: e.target.value })}
                        disabled={!canEdit}
                        data-testid="fecha-ph-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                      Detalles de Prueba Hidrostática
                      {extinguisher?.stage_1_data?.requiere_prueba_hidrostatica === 'Sí' && (
                        <span className="text-destructive"> *</span>
                      )}
                    </Label>
                    <Textarea
                      value={stageData.detalles_prueba_hidrostatica || ''}
                      onChange={(e) => canEdit && updateStageData({ ...stageData, detalles_prueba_hidrostatica: e.target.value })}
                      disabled={!canEdit}
                      placeholder="Ingrese los detalles de la prueba hidrostática..."
                      className="min-h-[80px]"
                      data-testid="detalles-ph-input"
                    />
                  </div>
                </div>

                {/* Observaciones */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                    Observaciones <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    value={stageData.observaciones || ''}
                    onChange={(e) => canEdit && updateStageData({ ...stageData, observaciones: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Observaciones adicionales sobre las refacciones requeridas..."
                    className="min-h-[100px]"
                    data-testid="observaciones-input"
                  />
                </div>

                {/* Número de Holograma */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                    Número de Holograma <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={stageData.numero_holograma || ''}
                    onChange={(e) => canEdit && updateStageData({ ...stageData, numero_holograma: e.target.value })}
                    disabled={!canEdit}
                    placeholder="Ingresa el número de holograma"
                    data-testid="numero-holograma-input"
                  />
                </div>
                
                {/* Firma del Técnico */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Firma del Técnico</Label>
                  {!canEdit && stageData.firma_tecnico ? (
                    <div className="border border-border p-2 bg-white">
                      <img src={stageData.firma_tecnico} alt="Firma del técnico" className="max-h-32 mx-auto" />
                    </div>
                  ) : !canEdit ? (
                    <p className="text-sm text-muted-foreground">Sin firma registrada</p>
                  ) : (
                    <>
                      <div className="border-2 border-dashed border-border bg-white">
                        <SignatureCanvas
                          ref={signatureRef}
                          canvasProps={{
                            className: 'w-full h-32 touch-none select-none',
                            'data-testid': 'signature-canvas'
                          }}
                          backgroundColor="white"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearSignature}
                        data-testid="clear-signature-button"
                      >
                        Limpiar Firma
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="p-8 border border-border bg-background">
            <PhotoUpload photos={photos} onPhotosChange={canEdit ? setPhotos : () => {}} maxPhotos={10} readOnly={!canEdit} />
          </div>

          {canEdit && (
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={safeNavigateBack}
                data-testid="cancel-button"
                className="hover:bg-secondary"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-stage-button"
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? 'Guardando...' : editMode ? `Guardar Cambios` : `Completar Etapa ${stageNum}`}
              </Button>
            </div>
          )}
          
          {viewOnly && !editMode && (
            <div className="flex gap-4">
              <Button
                type="button"
                onClick={() => navigate(`/orders/${extinguisher.order_id}`)}
                data-testid="back-to-order-button"
                className="bg-primary hover:bg-primary/90"
              >
                Volver a la Orden
              </Button>
            </div>
          )}
        </form>
      </div>
    </Layout>
  );
};
